import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import './kitchenView.css';
import IInvoiceTemp from '../../../main/interfaces/IInvoiceTemp';
import Loader from '../../../main/components/loader/Loader';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import axiosInstance from '../../../main/initializers/axios';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';

type KitchenTab = 1 | 2 | 3;

interface IKitchenInvoiceResult {
  productName: string | undefined;
  faturaTemps: IInvoiceTemp[];
  total: number;
}

const KitchenView = () => {
  const [activeTab, setActiveTab] = useState<KitchenTab>(1);
  const [items, setItems] = useState<IKitchenInvoiceResult[]>([]);
  const [loading, setLoading] = useState(false);

  const [modalState, setModalState] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<IInvoiceTemp[]>([]);

  const { i18n } = useTranslation();

  const fetchInvoices = useCallback((status: KitchenTab) => {
    setLoading(true);
    axiosInstance
      .get(`${ApiConstants.invoiceTemp.list}?status=${status}`)
      .then((res) => {
        setItems(res.data ?? []);
      })
      .finally(() => setLoading(false));
  }, []);

  const changeTab = (tab: KitchenTab) => {
    setActiveTab(tab);
    fetchInvoices(tab);
  };

  useEffect(() => {
    fetchInvoices(1);
  }, [fetchInvoices]);

  const modalLabel = useMemo(() => {
    if (activeTab === 1) return i18n.t('kitchen.toProcess');
    if (activeTab === 2) return i18n.t('kitchen.toComplete');
    return i18n.t('kitchen.toDeleteGroup');
  }, [activeTab]);

  const openApproveModal = (temps: IInvoiceTemp[]) => {
    setSelectedInvoice(temps ?? []);
    setModalState(true);
  };

  const approvo = () => {
    if (!selectedInvoice || selectedInvoice.length === 0) return;

    const ids = selectedInvoice.map((x) => x.id);
    axiosInstance.post(ApiConstants.invoiceTemp.updateInvoicesTemp, ids).then(() => {
      setModalState(false);
      fetchInvoices(activeTab);
    });
  };

  const refresh = () => {
    fetchInvoices(activeTab);
  };

  return (
    <BasicCli>
      {loading ? <Loader /> : null}

      <div className="kvPage">
        {/* Top bar */}
        <div className="ui-panel kvTopbar">
          <div className="kvTabs">
            <button onClick={() => changeTab(1)} className={`ui-control kvTabBtn ${activeTab === 1 ? 'isActive' : ''}`} type="button">
              {i18next.t('kitchen.active')}
            </button>

            <button onClick={() => changeTab(2)} className={`ui-control kvTabBtn ${activeTab === 2 ? 'isActive' : ''}`} type="button">
              {i18next.t('kitchen.inProcess')}
            </button>

            <button onClick={() => changeTab(3)} className={`ui-control kvTabBtn ${activeTab === 3 ? 'isActive' : ''}`} type="button">
              {i18next.t('kitchen.completed')}
            </button>
          </div>

          <button className="ui-control ui-btn ui-btn--primary kvRefresh" type="button" onClick={refresh}>
            {i18next.t('kitchen.refresh')}
          </button>
        </div>

        {/* Content */}
        <div className="kvContent">
          <div className="kvGrid">
            {items?.map((item, index) => (
              <div
                key={index}
                className={`bcCard kvCard kvCard--${activeTab}`}
                role="button"
                tabIndex={0}
                onClick={() => openApproveModal(item.faturaTemps)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openApproveModal(item.faturaTemps);
                }}
              >
                <div className="kvCardHeader">
                  <h3 className="kvTitle">{item.productName}</h3>
                  <span className="kvTotalBadge">
                    {' '}
                    {i18n.t('generic.total')} {item.total}
                  </span>
                </div>

                <div className="kvList" aria-label="Invoices">
                  {item.faturaTemps.map((ft: IInvoiceTemp, i: number) => (
                    <div key={i} className="kvRow">
                      <span className="kvTime">{ft?.timeCalculated ?? 0}</span>
                      <span className="kvUser">{ft?.user?.firstName ?? '-'}</span>
                      <span className="kvTable">{ft?.table?.code ?? '-'}</span>
                      <span className="kvQty">{ft?.quantityCalculated ?? 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal */}
        {modalState ? (
          <GeneralModal
            width="20vh"
            minwidth="400px"
            headerName={null}
            headerColor={'green'}
            handleClick={() => approvo()}
            handleClose={() => setModalState(false)}
          >
            <div className="kvModalBody">
              <label>{modalLabel}</label>
            </div>
          </GeneralModal>
        ) : null}
      </div>
    </BasicCli>
  );
};

export default KitchenView;
